/* ==========================================================================
   WARRIOR PANEL MOTION (Ian, 2026-10-04)

   WebGL open/close effects for the mobile menu, the basket (UpCart) and the
   filter drawer. Theme settings > Warrior motion picks one per panel;
   snippets/custom.globals.liquid writes the choice as
   html[data-custom-menu-fx|basket-fx|filter-fx] and loads this file only when
   at least one is a WebGL effect. Mockups: theme/docs/ux-review-2026-10-04/
   mockups/mobile-motion/ (https://claude.ai/artifact/56GH6TkTGh8JeAv9sAYw4W).

   NO VENDOR EDITS. Horizon's drawers stay in charge of opening, closing,
   focus, scroll lock and Escape. This file watches their state and paints
   over them:
     - "Mask" effects (ink, shatter, halftone, tunnel) make the panel's own
       background transparent while they run, and a canvas BEHIND the panel's
       content paints the panel colour in the effect's shape. The real menu
       or filter form fades in on top, so it is live and clickable at once.
     - Signal glitch keeps the panel visible and tears it in horizontal bands
       (clip-path), with RGB-split text and a canvas of scanline bars on top.
   Horizon waits for the panel's CSS animation before it hides a closing
   panel; the "PANEL MOTION" block in custom.css gives the closing panel a
   hold animation of the same length, so the close effect gets to play.

   Motion is a spring (stiffness 170, damping 17) on open, so it overshoots
   slightly and settles; close is a quick ease-in. Visitors who turn motion
   off, or browsers without WebGL, get Horizon's own drawers.
   ========================================================================== */
(function () {
  'use strict';
  var root = document.documentElement;
  var CLOSE_MS = 380;
  var gl0 = document.createElement('canvas').getContext('webgl', { premultipliedAlpha: true });
  if (!gl0 || matchMedia('(prefers-reduced-motion: reduce)').matches) {
    ['menu', 'basket', 'filter'].forEach(function (k) { root.dataset['custom' + k[0].toUpperCase() + k.slice(1) + 'Fx'] = 'none'; });
    return;
  }

  /* ---------- shaders: each outputs the panel colour (col) with alpha ---------- */
  var HEAD = [
    'precision mediump float;varying vec2 v;uniform float p,t,asp;uniform vec2 o;uniform vec3 col,c2;',
    'float h(vec2 q){return fract(sin(dot(q,vec2(127.1,311.7)))*43758.5453);}',
    'float n(vec2 q){vec2 i=floor(q),f=fract(q);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}',
    'float fbm(vec2 q){float s=0.,w=.5;for(int i=0;i<5;i++){s+=w*n(q);q*=2.03;w*=.5;}return s;}',
    'const float PI=3.14159265;',
    'float dist(vec2 u){vec2 q=u-o;q.x*=asp;return length(q);}',
    'vec2 rot(vec2 q,float a){float c=cos(a),s=sin(a);return vec2(c*q.x-s*q.y,s*q.x+c*q.y);}'
  ].join('\n');
  var FX = {
    /* ink bleeding out of the trigger along a noisy edge */
    ink: 'float P=clamp(p,0.,1.);float th=fbm(v*vec2(3.,6.)+vec2(0.,t*.05))*.55+dist(v)*.75;float a=smoothstep(P*1.6,P*1.6-.035,th);gl_FragColor=vec4(col*a,a);',
    /* dots grow out of the trigger and merge, like print */
    halftone: 'float P=clamp(p,0.,1.);float cells=30.;vec2 g=vec2(v.x*cells,v.y*cells/asp);vec2 gv=fract(g)-.5;vec2 id=floor(g);vec2 cc=vec2((id.x+.5)/cells,(id.y+.5)*asp/cells);float r=clamp(P*1.9-dist(cc)*1.1,0.,1.)*.75;float a=max((1.-smoothstep(r-.06,r,length(gv)))*step(.001,P),smoothstep(.86,1.,P));gl_FragColor=vec4(col*a,a);',
    /* two triangular shards per cell fly in, spinning, and lock together */
    shatter: 'float P=clamp(p,0.,1.)*1.2;vec2 G=vec2(7.,15.);vec2 id=floor(v*G);vec2 lu=fract(v*G);bool up=h(id+.7)>.5;float a=0.,rim=0.;for(int k=0;k<2;k++){vec2 c=k==0?(up?vec2(.33,.67):vec2(.33,.33)):(up?vec2(.67,.33):vec2(.67,.67));vec2 cc=(id+c)/G;float del=dist(cc)*.5+h(id+float(k)*3.1)*.18;float e=clamp((P-del)/.42,0.,1.);e=1.-pow(1.-e,3.);float sc=max(e,.001);float an=(1.-e)*(h(id+float(k))-.5)*3.;vec2 off=vec2(0.,-(1.-e)*.7);vec2 q=rot(lu-c-off,-an)/sc+c;bool inT=k==0?(up?q.y>q.x:q.x+q.y<1.):(up?q.y<q.x:q.x+q.y>1.);float inB=step(0.,q.x)*step(q.x,1.)*step(0.,q.y)*step(q.y,1.);if(inT&&inB>0.&&e>0.){a=1.;float ed=min(min(q.x,1.-q.x),min(q.y,1.-q.y));ed=min(ed,abs(up?q.y-q.x:q.x+q.y-1.)*.7);rim=(1.-smoothstep(0.,.06,ed))*(1.-e);}}vec3 cl=mix(col,vec3(1.),rim*.6);gl_FragColor=vec4(cl*a,a);',
    /* the panel ground zooms out of the trigger, speed lines rush past it */
    tunnel: 'float P=clamp(p,0.,1.);vec2 q=v-o;q.x*=asp;float z=P*P*(3.-2.*P);vec2 lo=-o*vec2(asp,1.)*z,hi=(1.-o)*vec2(asp,1.)*z;float rr=.06*(1.-z);vec2 cen=(lo+hi)*.5,hs=(hi-lo)*.5;vec2 dq=abs(q-cen)-hs+rr;float sd=length(max(dq,0.))+min(max(dq.x,dq.y),0.)-rr;float a=step(sd,0.);float ang=atan(q.y,q.x);float ray=step(.93,h(vec2(floor(ang*60./PI),3.)));float L=length(q);float head=fract(L*1.3-t*2.8+h(vec2(floor(ang*60./PI),9.)));float s=sin(PI*P);float line=ray*smoothstep(.0,.25,head)*(1.-a)*s*.85;float dim=(1.-a)*s*.35;vec3 cl=col*a+c2*line;float al=max(a,max(line,dim));gl_FragColor=vec4(cl*(a+line)+vec3(0.)*dim,al);',
    /* scanline bars for the signal glitch, drawn OVER the panel */
    glitch: 'float s=sin(PI*clamp(p,0.,1.));float fr=floor(t*20.);float row=floor(v.y*46.);float on=step(.8,h(vec2(row,fr)))*s;float x0=h(vec2(row,fr+3.));float w=.12+.55*h(vec2(row,fr+7.));float inx=step(abs(v.x-x0),w*.5);float pick=h(vec2(row,fr+11.));vec3 c=pick<.4?vec3(1.,.1,.35):(pick<.8?vec3(.1,.95,1.):c2);float bar=on*inx;vec2 bl=floor(v*vec2(22.,64.));float spk=step(.988,h(bl+fr))*s;float scan=s*.14*step(.5,fract(v.y*220.));vec3 cl=c*bar+vec3(1.)*spk;float a=max(max(bar*.8,spk),scan);gl_FragColor=vec4(cl*max(bar*.8,spk),a);'
  };
  var VS = 'attribute vec2 q;varying vec2 v;void main(){v=q*.5+.5;v.y=1.-v.y;gl_Position=vec4(q,0.,1.);}';

  function rgb(str) {
    var m = String(str).match(/[\d.]+/g) || [0, 0, 0];
    return [m[0] / 255, m[1] / 255, m[2] / 255];
  }
  function cssVar(name, fallback) {
    var v = getComputedStyle(root).getPropertyValue(name).trim();
    if (!v) return fallback;
    var d = document.createElement('i'); d.style.color = v; document.body.appendChild(d);
    var c = getComputedStyle(d).color; d.remove(); return rgb(c);
  }

  /* One canvas + program per run; small, created on demand, dropped after. */
  function Painter(key, host, fixedRect) {
    var cv = document.createElement('canvas');
    cv.className = 'custom-pfx-canvas';
    cv.setAttribute('aria-hidden', 'true');
    var gl = cv.getContext('webgl', { premultipliedAlpha: true, antialias: false });
    var pr = gl.createProgram();
    [[gl.VERTEX_SHADER, VS], [gl.FRAGMENT_SHADER, HEAD + '\nvoid main(){' + FX[key] + '}']].forEach(function (s) {
      var sh = gl.createShader(s[0]); gl.shaderSource(sh, s[1]); gl.compileShader(sh); gl.attachShader(pr, sh);
    });
    gl.linkProgram(pr); gl.useProgram(pr);
    var b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(pr, 'q'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    var U = {}; ['p', 't', 'asp', 'o', 'col', 'c2'].forEach(function (k) { U[k] = gl.getUniformLocation(pr, k); });
    host.appendChild(cv);
    var t0 = performance.now();
    var api = {
      el: cv,
      size: function (r) {
        var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        if (fixedRect) { cv.style.left = r.left + 'px'; cv.style.top = r.top + 'px'; }
        cv.style.width = r.width + 'px'; cv.style.height = r.height + 'px';
        var w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
        if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; gl.viewport(0, 0, w, h); }
      },
      draw: function (p, o, col, c2) {
        gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
        gl.uniform1f(U.p, p); gl.uniform1f(U.t, (performance.now() - t0) / 1000);
        gl.uniform1f(U.asp, cv.width / cv.height); gl.uniform2f(U.o, o[0], o[1]);
        gl.uniform3fv(U.col, col); gl.uniform3fv(U.c2, c2);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      },
      kill: function () { var x = gl.getExtension('WEBGL_lose_context'); cv.remove(); if (x) x.loseContext(); }
    };
    return api;
  }

  /* Spring 0 -> 1 (open) or timed ease-in 1 -> 0 (close). */
  function animate(from, to, step, done) {
    var p = from, vel = 0, last = performance.now(), start = last, raf = 0, opening = to > from;
    function tick(now) {
      var dt = Math.min(0.033, (now - last) / 1000); last = now;
      if (opening) {
        var a = -170 * (p - 1) - 17 * vel; vel += a * dt; p += vel * dt;
        if (Math.abs(p - 1) < 0.002 && Math.abs(vel) < 0.01) p = 1;
      } else {
        var k = Math.min(1, (now - start) / CLOSE_MS); p = from * (1 - k * k);
      }
      step(p);
      if ((opening && p === 1) || (!opening && p <= 0)) { raf = 0; done(); return; }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return { stop: function () { cancelAnimationFrame(raf); }, value: function () { return p; } };
  }

  function centre(el, fallback) {
    if (!el) return fallback;
    var r = el.getBoundingClientRect();
    return r.width ? [r.left + r.width / 2, r.top + r.height / 2] : fallback;
  }

  /* ---------- a run on one panel ---------- */
  function Run(panel, fx, originPx, opts) {
    this.panel = panel; this.fx = fx; this.originPx = originPx; this.opts = opts || {};
    this.p = 0; this.anim = null; this.painter = null; this.saved = null;
  }
  Run.prototype.start = function () {
    var panel = this.panel, fx = this.fx;
    var cs = getComputedStyle(panel);
    this.col = rgb(cs.backgroundColor);
    if (cs.backgroundColor === 'rgba(0, 0, 0, 0)') this.col = cssVar('--warrior-ink', [0.08, 0.08, 0.08]);
    this.c2 = cssVar('--warrior-accent-fill', [0.91, 0.9, 0.3]);
    this.saved = { bg: panel.style.background, clip: panel.style.clipPath, tr: panel.style.transform, ts: panel.style.textShadow, tn: panel.style.transition };
    if (fx === 'glitch') {
      this.painter = Painter('glitch', document.body, true);
      this.painter.el.style.cssText = 'position:fixed;pointer-events:none;z-index:2147483647';
      panel.style.transition = 'none';
    } else {
      this.painter = Painter(fx, panel, false);
      this.painter.el.style.cssText = 'position:absolute;left:0;top:' + panel.scrollTop + 'px;z-index:-1;pointer-events:none';
      panel.style.background = 'transparent';
      this.kids = Array.prototype.filter.call(panel.children, function (c) { return !c.classList.contains('custom-pfx-canvas'); });
      this.kidStyles = this.kids.map(function (c) { return [c.style.opacity, c.style.transform, c.style.filter, c.style.transformOrigin]; });
    }
    panel.classList.add('custom-pfx-run');
  };
  Run.prototype.frame = function (p) {
    this.p = p;
    var panel = this.panel, r = panel.getBoundingClientRect();
    if (!r.width) return;
    var o = [(this.originPx[0] - r.left) / r.width, (this.originPx[1] - r.top) / r.height];
    this.painter.size(this.fx === 'glitch' ? r : { width: r.width, height: r.height });
    this.painter.draw(p, o, this.col, this.c2);
    var P = Math.max(0, Math.min(1, p));
    if (this.fx === 'glitch') {
      var s = Math.sin(Math.PI * P), seed = Math.floor(performance.now() / 50), N = 16, pts = [];
      for (var i = 0; i < N; i++) {
        var hh = Math.abs(Math.sin((i + 1) * 12.9898 + seed * 78.233) * 43758.5453) % 1;
        if (hh < P * 1.15 - 0.05) pts.push('0% ' + (i / N * 100) + '%', '100% ' + (i / N * 100) + '%', '100% ' + ((i + 1) / N * 100) + '%', '0% ' + ((i + 1) / N * 100) + '%');
      }
      panel.style.clipPath = P >= 0.97 ? '' : 'polygon(' + (pts.length ? pts.join(',') : '0 0,0 0,0 0') + ')';
      var j = (Math.abs(Math.sin(seed * 3.7)) % 1 - 0.5) * 22 * s;
      panel.style.transform = s > 0.02 ? 'translate3d(' + j.toFixed(1) + 'px,0,0)' : '';
      var d = (3 * s).toFixed(1);
      panel.style.textShadow = s > 0.05 ? d + 'px 0 rgb(255 30 90 / .75),-' + d + 'px 0 rgb(30 240 255 / .75)' : '';
    } else {
      var c = this.fx === 'tunnel' ? smooth(0.35, 0.95, P) : smooth(0.55, 1, P);
      var ox = this.originPx[0] - r.left, oy = this.originPx[1] - r.top + panel.scrollTop;
      this.kids.forEach(function (k) {
        k.style.opacity = c.toFixed(3);
        if (this.fx === 'tunnel') {
          k.style.transformOrigin = ox + 'px ' + oy + 'px';
          k.style.transform = 'scale(' + (0.35 + 0.65 * P).toFixed(4) + ')';
          k.style.filter = P < 0.98 ? 'blur(' + ((1 - P) * 10).toFixed(1) + 'px)' : '';
        }
      }, this);
    }
  };
  Run.prototype.play = function (to, done) {
    var self = this;
    if (this.anim) this.anim.stop();
    this.anim = animate(this.p, to, function (p) { self.frame(p); }, function () { self.anim = null; if (done) done(); });
  };
  Run.prototype.end = function () {
    if (this.anim) this.anim.stop();
    var s = this.saved, panel = this.panel;
    if (this.painter) this.painter.kill();
    if (s) {
      panel.style.background = s.bg; panel.style.clipPath = s.clip; panel.style.transform = s.tr;
      panel.style.textShadow = s.ts; panel.style.transition = s.tn;
    }
    if (this.kids) this.kids.forEach(function (k, i) {
      var v = this.kidStyles[i]; k.style.opacity = v[0]; k.style.transform = v[1]; k.style.filter = v[2]; k.style.transformOrigin = v[3];
    }, this);
    panel.classList.remove('custom-pfx-run');
  };
  function smooth(a, b, x) { var t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

  function fxOf(k) { return root.dataset[k] || 'none'; }

  /* ---------- MENU: Horizon's header-drawer (<details class="menu-open">) ---------- */
  function wireMenu() {
    var details = document.querySelector('header-drawer details.menu-drawer-container');
    if (!details) return;
    var run = null, wasOpen = details.classList.contains('menu-open');
    new MutationObserver(function () {
      var fx = fxOf('customMenuFx'), open = details.classList.contains('menu-open');
      if (open === wasOpen || fx === 'none' || !FX[fx]) { wasOpen = open; return; }
      wasOpen = open;
      var drawer = details.querySelector(':scope > .menu-drawer');
      if (!drawer) return;
      if (open) {
        if (run) run.end();
        run = new Run(drawer, fx, centre(details.querySelector('summary'), [24, 30]));
        run.start(); run.frame(0);
        run.play(1, function () { run.end(); run = null; });
      } else {
        if (!run) { run = new Run(drawer, fx, centre(details.querySelector('summary'), [24, 30])); run.start(); run.p = 1; }
        var r0 = run;
        r0.play(0, function () { r0.end(); if (run === r0) run = null; });
      }
    }).observe(details, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- FILTERS: theme-drawer <dialog> (#filters-drawer) ---------- */
  var lastFilterTap = null;
  document.addEventListener('pointerdown', function (e) {
    var b = e.target.closest && e.target.closest('.facets-toggle__button, [on\\:click*="filters-drawer"]');
    if (b) lastFilterTap = b;
  }, true);
  function wireFilters() {
    var drawer = document.getElementById('filters-drawer');
    var dialog = drawer && drawer.querySelector(':scope > dialog');
    if (!dialog) return;
    var run = null;
    new MutationObserver(function () {
      var fx = fxOf('customFilterFx');
      if (!FX[fx] || fx === 'glitch') return;
      var opening = dialog.classList.contains('theme-drawer__dialog--opening');
      var closing = dialog.classList.contains('theme-drawer__dialog--closing');
      var vis = Array.prototype.filter.call(document.querySelectorAll('.facets-toggle__button'), function (b) { return b.getBoundingClientRect().width > 0; })[0];
      var origin = centre(lastFilterTap && lastFilterTap.isConnected ? lastFilterTap : vis, [innerWidth / 2, innerHeight - 40]);
      if (opening && !run) {
        run = new Run(dialog, fx, origin);
        run.start(); run.frame(0);
        var r1 = run; r1.play(1, function () { r1.end(); if (run === r1) run = null; });
      } else if (closing) {
        if (!run) { run = new Run(dialog, fx, origin); run.start(); run.p = 1; }
        var r0 = run; r0.play(0, function () { r0.end(); if (run === r0) run = null; });
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['class'] });
  }

  /* Depth stack (CSS) scales the page about the middle of the SCREEN, not of
     the 3,000px page: hand it the viewport centre in page coordinates. */
  function wireDepth() {
    var drawer = document.getElementById('filters-drawer'), wrap = document.querySelector('.page-wrapper');
    if (!drawer || !wrap) return;
    new MutationObserver(function () {
      if (!drawer.hasAttribute('open')) return;
      var r = wrap.getBoundingClientRect();
      wrap.style.setProperty('--custom-pfx-origin', (innerWidth / 2 - r.left) + 'px ' + (innerHeight / 2 - r.top) + 'px');
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  /* ---------- BASKET: UpCart, inside #upCart's open shadow root ---------- */
  function wireBasket() {
    var host = document.getElementById('upCart');
    if (!host || !host.shadowRoot) return false;
    var sr = host.shadowRoot, run = null, wasOpen = !!sr.querySelector('.upcartPopupShow .upcart-cart');
    new MutationObserver(function () {
      var fx = fxOf('customBasketFx');
      var cart = sr.querySelector('.upcart-cart');
      if (!cart) return;
      var open = !!cart.parentElement && cart.parentElement.classList.contains('upcartPopupShow');
      if (open === wasOpen) return;
      wasOpen = open;
      if (fx !== 'glitch') return;
      var origin = centre(document.querySelector('.header-actions__action.action__cart'), [innerWidth - 24, 30]);
      if (open) {
        if (run) run.end();
        run = new Run(cart, 'glitch', origin);
        run.start(); run.frame(0);
        var r1 = run; r1.play(1, function () { r1.end(); if (run === r1) run = null; });
      } else {
        /* UpCart slides the cart out on `right` the moment its class drops.
           Pin it in place for the glitch, then let it go with no slide. */
        if (run) run.end();
        run = new Run(cart, 'glitch', origin);
        run.start(); run.p = 1;
        cart.style.setProperty('right', '0px', 'important');
        var r0 = run;
        r0.play(0, function () {
          r0.end(); cart.style.transition = 'none'; cart.style.removeProperty('right');
          requestAnimationFrame(function () { requestAnimationFrame(function () { cart.style.transition = ''; }); });
          if (run === r0) run = null;
        });
      }
    }).observe(sr, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    return true;
  }

  function init() {
    wireMenu(); wireFilters(); wireDepth();
    /* UpCart mounts after load; try for 20s. */
    var tries = 0;
    (function poll() { if (!wireBasket() && tries++ < 40) setTimeout(poll, 500); })();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
